import type { History, LocationDescriptor } from 'history';

/**
 * Keeps secret tokens out of the address bar, where every third-party script
 * (GA4, GTM tags, Sentry, HubSpot) reads them, and hands them back to routes
 * via `RouteConfig.parseParams`. Scrubbed on page load by the first script in
 * `templates/index.html`, and on navigation by `scrubSensitiveUrlsOnNavigate`.
 *
 * Inlined into `index.html` as source, so it must stay self-contained. OE-1393.
 */
export const createSensitiveUrlVault = () => {
  const PLACEHOLDER = 'REDACTED';
  const STORAGE_KEY = 'studio:sensitiveUrlParams';
  const GLOBAL_KEY = '__studioSensitiveUrlParams';
  // Every token param a Studio route config reads.
  const SENSITIVE_QUERY_PARAMS = [
    'token',
    'joinToken',
    'inviteToken',
    'resetToken',
  ];
  // `/org/:orgId/invite/:joinToken`, optionally behind an origin.
  const INVITE_PATH =
    /^((?:[a-z][a-z0-9+.-]*:\/\/[^/?#]*)?\/org\/[^/?#]+\/invite\/)([^/?#]+)/i;
  const INVITE_KEY = 'path:invite';
  // Depth of redirect targets to search, e.g. `/login?from=/org/a/invite/<token>`.
  const MAX_NESTING = 3;

  type Secrets = Record<string, string>;

  const queryKey = (name: string) => `query:${name.toLowerCase()}`;

  const isSensitiveQueryParam = (name: string) =>
    SENSITIVE_QUERY_PARAMS.some(
      (param) => param.toLowerCase() === name.toLowerCase(),
    );

  const decode = (value: string) => {
    try {
      return decodeURIComponent(value.replace(/\+/g, ' '));
    } catch {
      return value;
    }
  };

  const splitHref = (href: string) => {
    const hashStart = href.indexOf('#');
    const beforeHash = hashStart === -1 ? href : href.slice(0, hashStart);
    const queryStart = beforeHash.indexOf('?');
    return {
      path: queryStart === -1 ? beforeHash : beforeHash.slice(0, queryStart),
      query: queryStart === -1 ? null : beforeHash.slice(queryStart + 1),
      hash: hashStart === -1 ? '' : href.slice(hashStart),
    };
  };

  const joinHref = (path: string, query: string | null, hash: string) =>
    `${path}${query === null ? '' : `?${query}`}${hash}`;

  type Scrubbed = { href: string; secrets: Secrets };

  const scrubPair = (pair: string, nesting: number): Scrubbed => {
    const unchanged = { href: pair, secrets: {} };
    const separator = pair.indexOf('=');
    if (separator === -1) return unchanged;
    const rawName = pair.slice(0, separator);
    const rawValue = pair.slice(separator + 1);
    if (!rawValue || rawValue === PLACEHOLDER) return unchanged;

    const name = decode(rawName);
    if (isSensitiveQueryParam(name)) {
      return {
        href: `${rawName}=${PLACEHOLDER}`,
        secrets: { [queryKey(name)]: decode(rawValue) },
      };
    }

    if (nesting >= MAX_NESTING) return unchanged;
    const value = decode(rawValue);
    const nested = scrubHref(value, nesting + 1);
    return nested.href === value
      ? unchanged
      : {
          href: `${rawName}=${encodeURIComponent(nested.href)}`,
          secrets: nested.secrets,
        };
  };

  const scrubHref = (href: string, nesting: number): Scrubbed => {
    const { path, query, hash } = splitHref(href);
    const secrets: Secrets = {};

    let scrubbedPath = path;
    const invite = INVITE_PATH.exec(path);
    if (invite && invite[2] !== PLACEHOLDER) {
      secrets[INVITE_KEY] = decode(invite[2]);
      scrubbedPath = `${invite[1]}${PLACEHOLDER}${path.slice(invite[0].length)}`;
    }

    let scrubbedQuery = query;
    if (query !== null) {
      const pairs = query.split('&').map((pair) => scrubPair(pair, nesting));
      pairs.forEach((pair) => Object.assign(secrets, pair.secrets));
      scrubbedQuery = pairs.map((pair) => pair.href).join('&');
    }

    return {
      href:
        scrubbedPath === path && scrubbedQuery === query
          ? href
          : joinHref(scrubbedPath, scrubbedQuery, hash),
      secrets,
    };
  };

  const readSecrets = (): Secrets => {
    const globals = window as unknown as Record<string, Secrets | undefined>;
    const cached = globals[GLOBAL_KEY];
    if (cached) return cached;

    let stored: Secrets = {};
    try {
      stored = JSON.parse(
        window.sessionStorage.getItem(STORAGE_KEY) || '{}',
      ) as Secrets;
    } catch {
      // Storage blocked: the in-memory copy still covers this page.
    }
    globals[GLOBAL_KEY] = stored;
    return stored;
  };

  const rememberSecrets = (secrets: Secrets) => {
    const all = Object.assign(readSecrets(), secrets);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    } catch {
      // As above.
    }
  };

  /** Returns `href` with secrets replaced and remembered, or `href` itself. */
  const scrub = (href: string) => {
    const scrubbed = scrubHref(href, 0);
    if (scrubbed.href !== href) rememberSecrets(scrubbed.secrets);
    return scrubbed.href;
  };

  const scrubWindowLocation = () => {
    const href = window.location.href;
    const scrubbed = scrub(href);
    if (scrubbed !== href) {
      window.history.replaceState(window.history.state, '', scrubbed);
    }
  };

  /** Unknown placeholders (e.g. a shared scrubbed URL) are left as is. */
  const restore = (location: { pathname: string; search: string }) => {
    const secrets = readSecrets();

    let pathname = location.pathname;
    const invite = INVITE_PATH.exec(pathname);
    const inviteToken = secrets[INVITE_KEY];
    if (invite && invite[2] === PLACEHOLDER && inviteToken !== undefined) {
      pathname = `${invite[1]}${encodeURIComponent(inviteToken)}${pathname.slice(invite[0].length)}`;
    }

    const search = location.search.replace(
      new RegExp(`([?&])([^=&#]+)=${PLACEHOLDER}(?=&|#|$)`, 'g'),
      (match, separator: string, rawName: string) => {
        const secret = secrets[queryKey(decode(rawName))];
        return secret === undefined
          ? match
          : `${separator}${rawName}=${encodeURIComponent(secret)}`;
      },
    );

    return { pathname, search };
  };

  return {
    SENSITIVE_QUERY_PARAMS,
    scrub,
    scrubWindowLocation,
    restore,
  };
};

export const sensitiveUrlVault = createSensitiveUrlVault();

/** Mutates `history` rather than copying it: `location` is a live property. */
export function scrubSensitiveUrlsOnNavigate(history: History): History {
  const scrubSearch = (search: string) => {
    const withMark = search.startsWith('?') ? search : `?${search}`;
    const scrubbed = sensitiveUrlVault.scrub(withMark);
    return scrubbed === withMark ? search : scrubbed;
  };

  const scrubTo = (to: LocationDescriptor): LocationDescriptor => {
    if (typeof to === 'string') return sensitiveUrlVault.scrub(to);

    // Scrub parts, not a serialized path: a missing pathname means "relative".
    const pathname = to.pathname && sensitiveUrlVault.scrub(to.pathname);
    const search = to.search && scrubSearch(to.search);
    return pathname === to.pathname && search === to.search
      ? to
      : { ...to, pathname, search };
  };

  const { push, replace } = history;
  return Object.assign(history, {
    push: (to: LocationDescriptor, state?: unknown) => push(scrubTo(to), state),
    replace: (to: LocationDescriptor, state?: unknown) =>
      replace(scrubTo(to), state),
  });
}
